import { supabase } from './supabase'
import { SprintReport, SprintDetails } from '../pages/SprintReport'

export interface SprintReportRecord {
  id: string
  user_id: string
  board_id: string
  sprint_label: string
  sprint_details: SprintDetails
  report_data: SprintReport
  analysis_log: any
  created_at: string
  updated_at: string
}

export class SprintReportService {
  
  // Save a sprint report to the database
  async saveReport(
    boardId: string,
    sprintLabel: string,
    sprintDetails: SprintDetails,
    report: SprintReport
  ): Promise<string> {
    try {
      console.log('💾 Saving sprint report to database...')
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('sprint_reports')
        .insert({
          user_id: user.id,
          board_id: boardId,
          sprint_label: sprintLabel,
          sprint_details: sprintDetails,
          report_data: report,
          analysis_log: report.analysisLog
        })
        .select('id')
        .single()

      if (error) {
        console.error('❌ Failed to save sprint report:', error)
        throw new Error('Failed to save sprint report')
      }

      console.log('✅ Sprint report saved with ID:', data.id)
      return data.id
    } catch (error) {
      console.error('❌ Error saving sprint report:', error)
      throw error
    }
  }

  // Get all sprint reports for the current user
  async getReports(): Promise<SprintReportRecord[]> {
    try {
      console.log('📋 Fetching sprint reports...')
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('sprint_reports')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('❌ Failed to fetch sprint reports:', error)
        throw new Error('Failed to fetch sprint reports')
      }

      console.log(`✅ Fetched ${data?.length || 0} sprint reports`)
      return data || []
    } catch (error) {
      console.error('❌ Error fetching sprint reports:', error)
      throw error
    }
  }

  // Get a specific sprint report by ID
  async getReport(reportId: string): Promise<SprintReportRecord | null> {
    try {
      console.log('📋 Fetching sprint report:', reportId)
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('sprint_reports')
        .select('*')
        .eq('id', reportId)
        .eq('user_id', user.id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          console.log('📋 Report not found')
          return null
        }
        console.error('❌ Failed to fetch sprint report:', error)
        throw new Error('Failed to fetch sprint report')
      }

      console.log('✅ Sprint report fetched successfully')
      return data
    } catch (error) {
      console.error('❌ Error fetching sprint report:', error)
      throw error
    }
  }

  // Delete a sprint report
  async deleteReport(reportId: string): Promise<void> {
    try {
      console.log('🗑️ Deleting sprint report:', reportId)
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { error } = await supabase
        .from('sprint_reports')
        .delete()
        .eq('id', reportId)
        .eq('user_id', user.id)

      if (error) {
        console.error('❌ Failed to delete sprint report:', error)
        throw new Error('Failed to delete sprint report')
      }

      console.log('✅ Sprint report deleted successfully')
    } catch (error) {
      console.error('❌ Error deleting sprint report:', error)
      throw error
    }
  }

  // Generate a shareable link for a report
  async generateShareableLink(reportId: string): Promise<string> {
    try {
      const report = await this.getReport(reportId)
      if (!report) {
        throw new Error('Report not found')
      }

      // In a real implementation, you might:
      // 1. Create a public shareable record
      // 2. Generate a unique token
      // 3. Return a public URL
      
      // For now, we'll create a simple data URL
      const shareableData = {
        id: report.id,
        sprint: report.sprint_label,
        board: report.board_id,
        summary: report.report_data.summary,
        insights: report.report_data.insights,
        retrospective: report.report_data.retrospective,
        generatedAt: report.created_at
      }

      const dataUrl = `data:application/json;base64,${btoa(JSON.stringify(shareableData))}`
      return dataUrl
    } catch (error) {
      console.error('❌ Error generating shareable link:', error)
      throw error
    }
  }

  // Get reports by board
  async getReportsByBoard(boardId: string): Promise<SprintReportRecord[]> {
    try {
      console.log('📋 Fetching sprint reports for board:', boardId)
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('sprint_reports')
        .select('*')
        .eq('user_id', user.id)
        .eq('board_id', boardId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('❌ Failed to fetch board reports:', error)
        throw new Error('Failed to fetch board reports')
      }

      console.log(`✅ Fetched ${data?.length || 0} reports for board`)
      return data || []
    } catch (error) {
      console.error('❌ Error fetching board reports:', error)
      throw error
    }
  }

  // Get reports by sprint label
  async getReportsBySprintLabel(sprintLabel: string): Promise<SprintReportRecord[]> {
    try {
      console.log('📋 Fetching sprint reports for label:', sprintLabel)
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('sprint_reports')
        .select('*')
        .eq('user_id', user.id)
        .eq('sprint_label', sprintLabel)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('❌ Failed to fetch label reports:', error)
        throw new Error('Failed to fetch label reports')
      }

      console.log(`✅ Fetched ${data?.length || 0} reports for label`)
      return data || []
    } catch (error) {
      console.error('❌ Error fetching label reports:', error)
      throw error
    }
  }
}

export const sprintReportService = new SprintReportService() 